import { RefObject, useCallback, useEffect, useState } from 'react';

interface StickyTableHeaderMetrics {
  visible: boolean;
  containerWidth: number;
  tableWidth: number;
  headerHeight: number;
  columnWidths: number[];
}

interface UseStickyTableHeaderOptions {
  enabled: boolean;
  stickyOffset: number;
  containerRef: RefObject<HTMLDivElement>;
  tableRef: RefObject<HTMLTableElement>;
  sentinelRef: RefObject<HTMLDivElement>;
  floatingHeaderRef: RefObject<HTMLDivElement>;
  translateInnerRef: RefObject<HTMLDivElement>;
}

const initialMetrics: StickyTableHeaderMetrics = {
  visible: false,
  containerWidth: 0,
  tableWidth: 0,
  headerHeight: 0,
  columnWidths: [],
};

const roundLayoutValue = (value: number) => Math.round(value * 100) / 100;

const areNumberArraysEqual = (left: number[], right: number[]) => {
  if (left.length !== right.length) return false;

  return left.every((value, index) => value === right[index]);
};

const getMeasuredColumnWidths = (table: HTMLTableElement) => {
  const cols = Array.from(table.querySelectorAll('colgroup col'));
  const colWidths = cols.map((col) => roundLayoutValue(col.getBoundingClientRect().width));

  if (colWidths.length > 0 && colWidths.every((width) => width > 0)) {
    return colWidths;
  }

  const firstBodyRowCells = table.tBodies[0]?.rows[0]?.cells;
  if (firstBodyRowCells && firstBodyRowCells.length === cols.length) {
    return Array.from(firstBodyRowCells).map((cell) => roundLayoutValue(cell.getBoundingClientRect().width));
  }

  return [];
};

export function useStickyTableHeader({
  enabled,
  stickyOffset,
  containerRef,
  tableRef,
  sentinelRef,
  floatingHeaderRef,
  translateInnerRef,
}: UseStickyTableHeaderOptions) {
  const [metrics, setMetrics] = useState<StickyTableHeaderMetrics>(initialMetrics);

  const syncScrollLeft = useCallback(() => {
    const container = containerRef.current;
    const floatingHeader = floatingHeaderRef.current;
    const translateInner = translateInnerRef.current;

    if (!container || !translateInner) return;

    const scrollLeft = container.scrollLeft;
    translateInner.style.transform = `translateX(${-scrollLeft}px)`;
    floatingHeader?.style.setProperty('--pds-table-sticky-scroll-left', `${scrollLeft}px`);
  }, [containerRef, floatingHeaderRef, translateInnerRef]);

  const measure = useCallback(() => {
    if (!enabled) return;

    const container = containerRef.current;
    const table = tableRef.current;

    if (!container || !table) return;

    const nextColumnWidths = getMeasuredColumnWidths(table);
    const nextContainerWidth = roundLayoutValue(container.clientWidth);
    const nextTableWidth = roundLayoutValue(Math.max(table.scrollWidth, table.getBoundingClientRect().width));
    const nextHeaderHeight = roundLayoutValue(table.tHead?.getBoundingClientRect().height ?? 0);

    setMetrics((prevMetrics) => {
      const hasChanged =
        prevMetrics.containerWidth !== nextContainerWidth ||
        prevMetrics.tableWidth !== nextTableWidth ||
        prevMetrics.headerHeight !== nextHeaderHeight ||
        !areNumberArraysEqual(prevMetrics.columnWidths, nextColumnWidths);

      if (!hasChanged) return prevMetrics;

      return {
        ...prevMetrics,
        containerWidth: nextContainerWidth,
        tableWidth: nextTableWidth,
        headerHeight: nextHeaderHeight,
        columnWidths: nextColumnWidths,
      };
    });

    syncScrollLeft();
  }, [containerRef, enabled, syncScrollLeft, tableRef]);

  const recomputeVisible = useCallback(() => {
    if (!enabled) return;

    const sentinel = sentinelRef.current;
    const table = tableRef.current;

    if (!sentinel || !table) return;

    const sentinelTop = sentinel.getBoundingClientRect().top;
    const tableBottom = table.getBoundingClientRect().bottom;
    const headerHeight = table.tHead?.getBoundingClientRect().height ?? 0;
    const nextVisible = sentinelTop <= stickyOffset && tableBottom > stickyOffset + headerHeight;

    setMetrics((prevMetrics) =>
      prevMetrics.visible === nextVisible ? prevMetrics : { ...prevMetrics, visible: nextVisible },
    );
  }, [enabled, sentinelRef, stickyOffset, tableRef]);

  useEffect(() => {
    if (enabled) return;

    setMetrics(initialMetrics);
  }, [enabled]);

  useEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const container = containerRef.current;
    if (!container) return;

    let animationFrameId: number | null = null;

    const scheduleSync = () => {
      if (animationFrameId !== null) return;

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        syncScrollLeft();
      });
    };

    scheduleSync();
    container.addEventListener('scroll', scheduleSync, { passive: true });

    return () => {
      container.removeEventListener('scroll', scheduleSync);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [containerRef, enabled, syncScrollLeft]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !('ResizeObserver' in window)) return;

    const container = containerRef.current;
    const table = tableRef.current;

    if (!container || !table) return;

    const resizeObserver = new window.ResizeObserver(() => {
      measure();
      recomputeVisible();
    });

    resizeObserver.observe(container);
    resizeObserver.observe(table);

    if (table.tHead) {
      resizeObserver.observe(table.tHead);
    }

    Array.from(table.querySelectorAll('colgroup col')).forEach((col) => {
      resizeObserver.observe(col);
    });

    measure();
    recomputeVisible();

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, enabled, measure, recomputeVisible, tableRef]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    let animationFrameId: number | null = null;
    const scheduleRecompute = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        recomputeVisible();
      });
    };

    recomputeVisible();
    window.addEventListener('scroll', scheduleRecompute, { passive: true });
    window.addEventListener('resize', scheduleRecompute);

    let intersectionObserver: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      intersectionObserver = new window.IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          recomputeVisible();
        },
        {
          rootMargin: `-${stickyOffset}px 0px 0px 0px`,
          threshold: 0,
        },
      );
      intersectionObserver.observe(sentinel);
    }

    return () => {
      window.removeEventListener('scroll', scheduleRecompute);
      window.removeEventListener('resize', scheduleRecompute);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      intersectionObserver?.disconnect();
    };
  }, [enabled, recomputeVisible, sentinelRef, stickyOffset]);

  return {
    ...metrics,
    syncScrollLeft,
    measure,
  };
}
